import { CreateTaskListForm, EditTaskListForm, TaskListsService } from '@api'
import { z } from 'zod'

import { TaskSchema } from '@/modules/tasks/service/task-service'
import { apiClient } from '@/utils/axios'

export const ListSchema = z.object({
  id: z.number(),
  name: z.string(),
  color: z.string().nullish(),
  icon: z.string().nullish(),
  detail: z.string().nullish(),
  tasks: z.array(TaskSchema),
  teamId: z.number().nullish(),
})

export type List = z.infer<typeof ListSchema>

export const EditListSchema = z.object({
  name: z.string(),
  color: z.string().nullish(),
  icon: z.string().nullish(),
  detail: z.string().nullish(),
  tasks: z.array(z.number()),
})

export type ListForm = z.infer<typeof EditListSchema>

export const ListListResponseSchema = z.array(ListSchema)

export const DeleteTaskSchema = z.object({
  id: z.number(),
  contentPolicy: z.enum(['UNLINK', 'DELETE']).default('DELETE'),
})

export type DeleteTaskForm = z.infer<typeof DeleteTaskSchema>

function list() {
  return TaskListsService.listTaskLists().then((response) =>
    ListListResponseSchema.parse(response),
  )
}

function find(id: number) {
  return TaskListsService.findTaskList({ id }).then((response) =>
    ListSchema.parse(response),
  )
}

function update({ id, form }: { id: number; form: ListForm }) {
  const apiForm: EditTaskListForm = {
    name: form.name,
    detail: form.detail || undefined,
    color: form.color || undefined,
    icon: form.icon || undefined,
    tasks: form.tasks,
  }

  return TaskListsService.editTaskList({ id, requestBody: apiForm }).then(
    (response) => ListSchema.parse(response),
  )
}

function create(form: ListForm) {
  const apiForm: CreateTaskListForm = {
    name: form.name,
    detail: form.detail || undefined,
    color: form.color || '',
    icon: form.icon || '',
    tasks: form.tasks,
  }

  return TaskListsService.createTaskList({ requestBody: apiForm }).then(
    (response) => ListSchema.parse(response),
  )
}

function _delete({ id, contentPolicy }: DeleteTaskForm) {
  // TODO: The service doesn't support contentPolicy query param
  return apiClient.delete(
    `/projects/tasks/list/${id}?contentPolicy=${contentPolicy}`,
  )
}

function addTask({ task, list }: { task: number; list: number }) {
  return TaskListsService.addTaskToTaskList({
    id: list,
    requestBody: { taskId: task },
  }).then((response) => ListSchema.parse(response))
}

function removeTask({ task, list }: { task: number; list: number }) {
  return TaskListsService.removeTaskFromTaskList({
    id: list,
    requestBody: { taskId: task },
  }).then((response) => ListSchema.parse(response))
}

export const listService = {
  list,
  find,
  update,
  delete: _delete,
  create,
  addTask,
  removeTask,
} as const

export type ListService = typeof listService
